import React from 'react';
import { Link } from 'react-router-dom';
import { Job } from '../types';
import { jobUrl } from '../utils/slug';
import { formatJobExperience, hasExperienceRange } from '../utils/experienceRange';
import JobMatchBar from './JobMatchBar';

interface JobCardProps {
  job: Job;
  showMatchScore?: boolean;
  matchScore?: number;
  showCompany?: boolean;
  onClick?: () => void;
  showSaveAction?: boolean;
  isSaved?: boolean;
  saveLoading?: boolean;
  onToggleSave?: (jobId: number, shouldSave: boolean) => void;
  myMatch?: Job['my_match'];
}

const JobCard: React.FC<JobCardProps> = ({
  job,
  showMatchScore = false,
  matchScore,
  showCompany = true,
  onClick,
  showSaveAction = false,
  isSaved = false,
  saveLoading = false,
  onToggleSave,
  myMatch,
}) => {
  const effectiveMatch = myMatch ?? job.my_match;
  const formatSalary = (min?: number, max?: number, currency: string = 'INR') => {
    if (!min && !max) return 'CTC not disclosed';
    
    const format = (amount: number) => {
      if (currency === 'INR') {
        return `₹${(amount / 100000).toFixed(1)}L`;
      }
      return `${currency} ${amount.toLocaleString()}`;
    };

    if (min && max) {
      return `${format(min)} - ${format(max)}`;
    }
    
    return min ? `${format(min)}+` : `Up to ${format(max!)}`;
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 80) return '#22c55e';
    if (score >= 60) return '#fbbf24';
    return '#ef4444';
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };


  return (
    <div 
      className="card job-card"
      onClick={onClick}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <Link to={jobUrl(job)} className="h3" style={{ color: 'var(--ink)', textDecoration: 'none' }}>
            {job.title}
          </Link>
          
          {showCompany && job.company && (
            <p className="muted" style={{ margin: '4px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{job.company.name}</span>
              {(job as any).source_type === 'external' && (job as any).source_name && (
                <span className="chip" style={{ fontSize: '11px', padding: '2px 8px' }}>
                  via {(job as any).source_name}
                </span>
              )}
            </p>
          )}
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
            <span className="muted" style={{ fontSize: '14px' }}>
              {job.location || 'Remote'}
            </span>
            
            {job.job_type && (
              <>
                <span className="muted">•</span>
                <span className="muted" style={{ fontSize: '14px', textTransform: 'capitalize' }}>
                  {job.job_type.replace('_', ' ')}
                </span>
              </>
            )}
            
            {job.remote_allowed && (
              <>
                <span className="muted">•</span>
                <span className="badge green" style={{ fontSize: '11px' }}>
                  Remote Applications Accepted
                </span>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          {hasExperienceRange(job) && (
            <span className="badge blue">{formatJobExperience(job)}</span>
          )}

          {effectiveMatch && (
            <JobMatchBar
              jobId={job.id}
              initialScore={effectiveMatch.score}
              initialBracket={effectiveMatch.bracket}
              size="sm"
              loggedOutMode="hide"
            />
          )}

          {showMatchScore && typeof matchScore === 'number' && (
            <div className="match-score" style={{ minWidth: '120px' }}>
              <div className="score-bar">
                <div 
                  className="score-fill"
                  style={{ 
                    width: `${matchScore}%`,
                    background: getMatchScoreColor(matchScore)
                  }}
                />
              </div>
              <span style={{ fontSize: '12px', color: getMatchScoreColor(matchScore), fontWeight: '600' }}>
                {Math.round(matchScore)}%
              </span>
            </div>
          )}
        </div>
      </div>

      <p className="muted" style={{ margin: '12px 0', fontSize: '14px', lineHeight: '1.5' }}>
        {truncateText(job.description, 150)}
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
        <div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--accent-2)' }}>
            {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
          </div>
          
          {job.applications_count > 0 && (
            <div className="muted" style={{ fontSize: '12px', marginTop: '2px' }}>
              {job.applications_count} applicant{job.applications_count !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          {showSaveAction && (
            <button
              type="button"
              className="btn ghost"
              style={{ padding: '6px 12px', fontSize: '14px' }}
              disabled={saveLoading}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleSave?.(job.id, !isSaved);
              }}
            >
              {saveLoading ? 'Saving...' : isSaved ? 'Saved' : 'Save Job'}
            </button>
          )}
          <Link 
            to={jobUrl(job)}
            className="btn ghost"
            style={{ padding: '6px 12px', fontSize: '14px' }}
          >
            View Details
          </Link>
        </div>
      </div>

      {job.skills && job.skills.length > 0 && (
        <div className="skills-list">
          {job.skills.slice(0, 4).map((jobSkill) => (
            <span key={jobSkill.id} className="skill-tag">
              {jobSkill.skill?.name}
              {jobSkill.is_mandatory && '*'}
            </span>
          ))}
          {job.skills.length > 4 && (
            <span className="skill-tag" style={{ color: 'var(--accent)' }}>
              +{job.skills.length - 4} more
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', fontSize: '12px' }}>
        <span className="muted">
          Posted {new Date(job.created_at).toLocaleDateString()}
        </span>
        
        {job.expires_at && (
          <span className="muted">
            Expires {new Date(job.expires_at).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
};

export default JobCard;
